import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { posicaoDoPopover, type Ancora } from './calendario.ts'

/**
 * Popover ancorado no que foi clicado (design do calendário, 2026-10-31): o
 * evento, a célula ou o ponto da grade. Fica à direita do clique (ou à
 * esquerda, sem espaço) e se ajusta à altura da janela depois de medido.
 *
 * Com o zoom do Desktop (CSS `zoom` na raiz, `lib/zoom.ts`), o que vem da
 * tela (âncora, janela) está em px já ampliados, mas `left`/`top` são
 * ampliados de novo ao desenhar: tudo é convertido para px do documento antes.
 *
 * Fecha com Esc e com clique fora. O foco vai para dentro ao abrir e volta
 * para quem estava com ele ao fechar.
 */
export function Popover({
  ancora,
  largura,
  rotulo,
  onFechar,
  children,
}: {
  ancora: Ancora
  largura: number
  /** Nome acessível do diálogo. */
  rotulo: string
  onFechar: () => void
  children: ReactNode
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const [posicao, setPosicao] = useState<{ x: number; y: number; alturaMaxima: number } | null>(null)
  const fecharRef = useRef(onFechar)
  fecharRef.current = onFechar

  // mede depois de desenhar: a altura depende do conteúdo
  useLayoutEffect(() => {
    const el = caixa.current
    if (!el) return
    const medir = () => {
      const zoom = zoomDoDocumento()
      const janela = { largura: window.innerWidth / zoom, altura: window.innerHeight / zoom }
      const noDocumento = {
        left: ancora.left / zoom,
        top: ancora.top / zoom,
        right: ancora.right / zoom,
        bottom: ancora.bottom / zoom,
      }
      const alturaMaxima = janela.altura - 24
      const { x, y } = posicaoDoPopover(
        noDocumento,
        { largura: Math.min(largura, janela.largura - 24), altura: Math.min(el.offsetHeight, alturaMaxima) },
        janela,
      )
      setPosicao({ x, y, alturaMaxima })
    }
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(el)
    return () => observador.disconnect()
  }, [ancora, largura])

  // o foco entra quando a caixa já tem lugar (escondida, ela não aceita foco);
  // quem tem campo próprio para focar (criação rápida) foca o campo depois
  const posicionado = posicao !== null
  useEffect(() => {
    if (posicionado && !caixa.current?.contains(document.activeElement)) caixa.current?.focus({ preventScroll: true })
  }, [posicionado])

  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        fecharRef.current()
      }
    }
    const foraDaCaixa = (e: PointerEvent) => {
      const alvo = e.target as Node
      if (caixa.current?.contains(alvo)) return
      // listas do Select/TagSelect e menus ancorados abrem em portal: o clique nelas é "dentro"
      if ((alvo as HTMLElement).closest?.('.ob-select__popover--portal, .ob-anchored, .ob-anchored__scrim')) return
      fecharRef.current()
    }
    document.addEventListener('keydown', tecla, true)
    document.addEventListener('pointerdown', foraDaCaixa, true)
    return () => {
      document.removeEventListener('keydown', tecla, true)
      document.removeEventListener('pointerdown', foraDaCaixa, true)
      anterior?.focus?.({ preventScroll: true })
    }
  }, [])

  return createPortal(
    <div
      ref={caixa}
      className="cal-pop"
      role="dialog"
      aria-label={rotulo}
      tabIndex={-1}
      style={{
        width: largura,
        left: posicao?.x ?? -9999,
        top: posicao?.y ?? 0,
        maxHeight: posicao?.alturaMaxima,
        visibility: posicao ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}

/** Zoom do Desktop aplicado na raiz (1 no navegador, onde o zoom é do próprio browser). */
function zoomDoDocumento(): number {
  const zoom = (document.documentElement as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom
  return zoom && zoom > 0 ? zoom : 1
}

/** Âncora a partir do elemento clicado (ou do ponto do clique, quando não há elemento). */
export function ancoraDoClique(e: { currentTarget: EventTarget | null; clientX?: number; clientY?: number }): Ancora {
  const el = e.currentTarget as HTMLElement | null
  if (el?.getBoundingClientRect) {
    const r = el.getBoundingClientRect()
    return { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
  }
  const x = e.clientX ?? window.innerWidth / 2
  const y = e.clientY ?? window.innerHeight / 3
  return { left: x, top: y, right: x, bottom: y }
}

/** Âncora num ponto (clique na grade de horas: o popover nasce ao lado do ponteiro). */
export const ancoraNoPonto = (x: number, y: number): Ancora => ({ left: x, top: y + 72, right: x + 6, bottom: y })

/** Âncora no centro da tela (atalho "C", botão "Criar"). */
export const ancoraNoCentro = (): Ancora => {
  const x = window.innerWidth / 2 - 220
  const y = Math.max(80, window.innerHeight / 2 - 200)
  return { left: x, top: y + 8, right: x - 10, bottom: y }
}
